import React, { useEffect, useState, useMemo } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { ArrowLeft, ChevronLeft, ChevronRight, Crown, Calendar } from 'lucide-react'
import { useSelector } from 'react-redux'
import { useTranslation } from '../hooks/use-i18n'
import formula1Service from '../services/formula1Service'
import roomsService from '../services/roomsService'
import futureChampionService from '../services/futureChampionService'
import { Formula1Race, Formula1Prediction } from '../types/formula1'
import { pickUpcomingFormula1Races } from '../utils/formula1RaceTiming'
import { FutureChampionPrediction } from '../types/futureChampion'
import type { PredictExtraStep, RoomPredictionStepApi } from '../types/predictionSteps'
import Formula1SelectPositionModal from '../components/Formula1SelectPositionModal'
import Formula1ExistedPositionModal from '../components/Formula1ExistedPositionModal'
import FutureChampionExistedModal from '../components/FutureChampionExistedModal'
import F1GridCopilotPanel from '../components/agent/F1GridCopilotPanel'
import type { AgentF1GridProposal } from '../services/agentService'
import type { DriverOption } from '../components/Formula1SelectPositionModal'
import toast from 'react-hot-toast'
import { RootState } from '../store'

const POSITIONS = 10

const normalizeRace = (r: Formula1Race): Formula1Race => {
  const race = { ...r }
  if (typeof race.drivers === 'string') {
    try {
      race.drivers = JSON.parse(race.drivers)
    } catch {
      race.drivers = []
    }
  }
  if (typeof race.cars === 'string') {
    try {
      race.cars = JSON.parse(race.cars)
    } catch {
      race.cars = []
    }
  }
  if (!Array.isArray(race.drivers)) race.drivers = []
  if (!Array.isArray(race.cars)) race.cars = []
  return race
}

const Formula1Playing: React.FC = () => {
  const { roomId } = useParams<{ roomId: string }>()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { user } = useSelector((state: RootState) => state.auth)
  const playerId = user?._id ?? (user as any)?.id ?? ''
  const [races, setRaces] = useState<Formula1Race[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [selectedRaceIndex, setSelectedRaceIndex] = useState(0)
  const [selectedPositionIndex, setSelectedPositionIndex] = useState(0)
  const [positionToDriver, setPositionToDriver] = useState<Record<number, string>>({})
  const [prediction, setPrediction] = useState<Formula1Prediction | null>(null)
  const [isLoadingPrediction, setIsLoadingPrediction] = useState(false)
  const [editingPosition, setEditingPosition] = useState<number | null>(null)
  const [showSelectModal, setShowSelectModal] = useState(false)
  const [showExistedModal, setShowExistedModal] = useState(false)
  const [, setIsSaving] = useState(false)
  const [extraSteps, setExtraSteps] = useState<PredictExtraStep[]>([])
  const [extraIndex, setExtraIndex] = useState(0)
  const [futureChampionPredictionsMap, setFutureChampionPredictionsMap] = useState<Record<string, FutureChampionPrediction>>({})
  const [futureChampionEditMode, setFutureChampionEditMode] = useState(false)
  const [futureChampionSubmitting, setFutureChampionSubmitting] = useState(false)
  const [futureChampionQuery, setFutureChampionQuery] = useState('')
  const inExtras = extraSteps.length > 0 && extraIndex < extraSteps.length
  const currentExtra = inExtras ? extraSteps[extraIndex] : null
  const currentFcEvent = currentExtra?.stepType === 'future_champion' ? currentExtra.event : null
  const currentFcPrediction = currentFcEvent
    ? futureChampionPredictionsMap[currentFcEvent.id ?? (currentFcEvent as any)._id]
    : null

  const currentRace = races.length > 0 ? races[selectedRaceIndex] ?? null : null
  const raceId = currentRace?.id ?? currentRace?._id ?? ''
  const currentPosition = selectedPositionIndex + 1

  useEffect(() => {
    if (!roomId) return
    const load = async () => {
      try {
        setIsLoading(true)
        const data = await formula1Service.getRaces({ limit: 50 })
        const list = Array.isArray(data) ? data : []
        const normalized = list.map(normalizeRace)
        const withDrivers = normalized.filter((r) => r.drivers.length > 0)
        const upcoming = pickUpcomingFormula1Races(withDrivers)
        setRaces(upcoming)
        setSelectedRaceIndex(0)
        setSelectedPositionIndex(0)
        setPositionToDriver({})
        setPrediction(null)
        setEditingPosition(null)
      } catch (err) {
        console.error('Failed to load races:', err)
        toast.error('Failed to load races')
        setRaces([])
      } finally {
        setIsLoading(false)
      }
    }
    load()
  }, [roomId])

  useEffect(() => {
    if (!roomId || !playerId) return
    let cancelled = false
    Promise.all([
      roomsService.getPredictionSteps(roomId),
      futureChampionService.getPlayerPredictions(roomId, playerId),
    ])
      .then(([stepsApi, fcPreds]) => {
        if (cancelled) return
        const steps: PredictExtraStep[] = (stepsApi || [])
          .filter((s): s is Extract<RoomPredictionStepApi, { type: 'future_champion' }> => s.type === 'future_champion')
          .map((s) => ({ stepType: 'future_champion' as const, event: s.event }))
        setExtraSteps(steps)
        const fcMap: Record<string, FutureChampionPrediction> = {}
        ;(Array.isArray(fcPreds) ? fcPreds : []).forEach((p) => {
          const eid = p.eventId ?? (p.event as any)?.id
          if (eid) fcMap[eid] = p
        })
        setFutureChampionPredictionsMap(fcMap)
      })
      .catch(() => {
        if (!cancelled) setExtraSteps([])
      })
    return () => {
      cancelled = true
    }
  }, [roomId, playerId])

  useEffect(() => {
    setFutureChampionQuery('')
    setFutureChampionEditMode(false)
  }, [extraIndex])

  useEffect(() => {
    if (!roomId || !raceId) return
    const loadPrediction = async () => {
      setIsLoadingPrediction(true)
      setPositionToDriver({})
      setPrediction(null)
      setSelectedPositionIndex(0)
      setEditingPosition(null)
      try {
        const pred = await formula1Service.getPrediction(roomId, raceId)
        if (pred?.predictedPositions) {
          setPrediction(pred)
          const map: Record<number, string> = {}
          Object.entries(pred.predictedPositions).forEach(([driver, position]) => {
            map[Number(position)] = driver
          })
          setPositionToDriver(map)
        }
      } catch (e: any) {
        if (e.response?.status !== 404) console.error('Failed to load prediction', e)
      } finally {
        setIsLoadingPrediction(false)
      }
    }
    loadPrediction()
  }, [roomId, raceId])

  useEffect(() => {
    if (inExtras) return
    if (isLoadingPrediction || !currentRace) return
    if (selectedPositionIndex >= POSITIONS) return
    const hasExisting = positionToDriver[currentPosition]
    if (hasExisting && editingPosition !== currentPosition) {
      setShowExistedModal(true)
      setShowSelectModal(false)
    } else {
      setShowExistedModal(false)
      setShowSelectModal(true)
    }
  }, [selectedPositionIndex, positionToDriver, currentPosition, editingPosition, isLoadingPrediction, currentRace, inExtras])

  const availableDriversForPosition = useMemo((): DriverOption[] => {
    if (!currentRace) return []
    const drivers = Array.isArray(currentRace.drivers) ? currentRace.drivers : []
    const cars = Array.isArray(currentRace.cars) ? currentRace.cars : []
    const selectedElsewhere = Object.entries(positionToDriver)
      .filter(([pos]) => Number(pos) !== currentPosition)
      .map(([, driver]) => driver)
    const currentDriver = positionToDriver[currentPosition]
    return drivers
      .map((d, i) => ({ label: d, value: d, car: cars[i] || 'Unknown' }))
      .filter(
        (opt) => opt.value === currentDriver || !selectedElsewhere.includes(opt.value)
      )
  }, [currentRace, positionToDriver, currentPosition])

  const savePrediction = async (nextPositionToDriver: Record<number, string>) => {
    if (!roomId || !raceId) return
    const predictedPositions: Record<string, number> = {}
    Object.entries(nextPositionToDriver).forEach(([pos, driver]) => {
      predictedPositions[driver] = Number(pos)
    })
    try {
      setIsSaving(true)
      if (prediction) {
        await formula1Service.updatePrediction(roomId, raceId, { predictedPositions })
      } else {
        await formula1Service.createPrediction({ roomId, raceId, predictedPositions })
      }
      const pred = await formula1Service.getPrediction(roomId, raceId)
      setPrediction(pred ?? null)
      toast.success(t('formula1.predictionSaved'))
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to save prediction')
      throw err
    } finally {
      setIsSaving(false)
    }
  }

  const handleSelectDriver = async (driver: string) => {
    const next = { ...positionToDriver, [currentPosition]: driver }
    setPositionToDriver(next)
    setShowSelectModal(false)
    setEditingPosition(null)
    if (Object.keys(next).length === POSITIONS) {
      await savePrediction(next)
    }
    setSelectedPositionIndex((i) => Math.min(i + 1, POSITIONS))
  }

  const handleKeepPosition = () => {
    setShowExistedModal(false)
    setSelectedPositionIndex((i) => Math.min(i + 1, POSITIONS))
  }

  const handleChangePosition = () => {
    setShowExistedModal(false)
    setEditingPosition(currentPosition)
    setShowSelectModal(true)
  }

  const handleCloseSelectModal = () => {
    setShowSelectModal(false)
    setEditingPosition(null)
  }

  const completedAllForRace = selectedPositionIndex >= POSITIONS
  const hasRaces = races.length > 0
  const hasNextRace = selectedRaceIndex < races.length - 1

  const handleApplyF1Grid = (proposal: AgentF1GridProposal) => {
    const next: Record<number, string> = {}
    for (const [driver, pos] of Object.entries(proposal.predictedPositions)) {
      if (pos >= 1 && pos <= POSITIONS) next[pos] = driver
    }
    setPositionToDriver(next)
    toast.success(t('agent.applySuggestion'))
    if (Object.keys(next).length === POSITIONS) {
      void savePrediction(next)
      setSelectedPositionIndex(POSITIONS)
    }
  }

  const handleFcChoice = async (choice: string) => {
    if (!roomId || !currentFcEvent) return
    const eventId = currentFcEvent.id ?? (currentFcEvent as any)._id
    setFutureChampionSubmitting(true)
    try {
      const saved = await futureChampionService.createPrediction({
        roomId,
        eventId,
        predictedChampion: choice,
      })
      setFutureChampionPredictionsMap((prev) => ({ ...prev, [eventId]: saved }))
      setFutureChampionEditMode(false)
      setExtraIndex((i) => i + 1)
    } catch (e: any) {
      toast.error(e?.response?.data?.message || t('futureChampion.saveFailed') || 'Could not save')
    } finally {
      setFutureChampionSubmitting(false)
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500" />
      </div>
    )
  }

  if (inExtras && currentFcEvent) {
    const q = futureChampionQuery.trim().toLowerCase()
    const choices = (currentFcEvent.choices || []).filter((c) =>
      !q ? true : String(c).toLowerCase().includes(q),
    )
    const open = currentFcEvent.isActive && new Date() < new Date(currentFcEvent.eventDate)
    return (
      <div className="min-h-screen bg-background p-4 sm:p-6">
        <div className="max-w-2xl mx-auto">
          <Link
            to={`/formula1/${roomId}`}
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-4 sm:mb-6 text-small sm:text-body"
          >
            <ArrowLeft className="w-4 h-4 shrink-0" />
            {t('predictions.backToRoom')}
          </Link>
          <div className="blue-sky-card rounded-2xl p-6 sm:p-8 text-center">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-white/10 mb-3">
              <Crown className="w-7 h-7 text-yellow-400" />
            </div>
            <h1 className="text-xl sm:text-2xl font-bold text-white mb-1">{currentFcEvent.name}</h1>
            <div className="flex items-center justify-center gap-2 text-white/80 text-sm mb-6">
              <Calendar className="w-4 h-4" />
              <span>{new Date(currentFcEvent.eventDate).toLocaleDateString()}</span>
            </div>
            {!open ? (
              <p className="text-white/80">{t('futureChampion.closed') || 'Predictions closed for this event.'}</p>
            ) : (
              <>
                <input
                  type="search"
                  value={futureChampionQuery}
                  onChange={(e) => setFutureChampionQuery(e.target.value)}
                  placeholder={t('futureChampion.searchPlaceholder')}
                  className="w-full mb-3 px-4 py-2.5 rounded-xl bg-white/10 border border-white/20 text-white placeholder:text-white/50 text-sm"
                />
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
                  {choices.map((choice) => (
                    <button
                      key={String(choice)}
                      type="button"
                      disabled={futureChampionSubmitting}
                      onClick={() => handleFcChoice(String(choice))}
                      className="px-4 py-3 bg-primary hover:opacity-90 disabled:opacity-50 text-primary-foreground rounded-xl font-semibold text-sm"
                    >
                      {String(choice).replace(/^[\s"{]+|[\s"}]+$/g, '')}
                    </button>
                  ))}
                </div>
              </>
            )}
            <div className="flex items-center justify-center gap-4 mt-6">
              <button
                type="button"
                onClick={() => setExtraIndex((i) => Math.max(0, i - 1))}
                disabled={extraIndex === 0}
                className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-40 flex items-center justify-center text-white"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                type="button"
                onClick={() => setExtraIndex((i) => i + 1)}
                className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
            {currentFcPrediction && !futureChampionEditMode && (
              <FutureChampionExistedModal
                eventName={currentFcEvent.name}
                currentChoice={String(currentFcPrediction.predictedChampion).replace(/^[\s"{]+|[\s"}]+$/g, '')}
                onKeepClick={() => setExtraIndex((i) => i + 1)}
                onChangeClick={() => setFutureChampionEditMode(true)}
              />
            )}
          </div>
        </div>
      </div>
    )
  }

  if (!hasRaces) {
    return (
      <div className="min-h-screen bg-background p-4 sm:p-6">
        <div className="max-w-2xl mx-auto">
          <Link
            to={`/formula1/${roomId}`}
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-4 sm:mb-6 text-small sm:text-body"
          >
            <ArrowLeft className="w-4 h-4 shrink-0" />
            {t('predictions.backToRoom')}
          </Link>
          <div className="blue-sky-card rounded-2xl p-6 sm:p-8 text-center">
            <div className="text-6xl mb-6">🏎️</div>
            <h2 className="text-xl sm:text-2xl font-bold text-white mb-4">
              {t('formula1.playingNoRaces')}
            </h2>
            <p className="text-white/80 text-small sm:text-body mb-6">
              {t('formula1.playingNoRacesNotify')}
            </p>
            <Link
              to={`/formula1/${roomId}`}
              className="inline-block px-6 sm:px-8 py-2.5 sm:py-3 bg-primary hover:opacity-90 text-primary-foreground rounded-xl font-semibold transition-opacity text-small sm:text-nav"
            >
              {t('predictions.backToRoomButton')}
            </Link>
          </div>
        </div>
      </div>
    )
  }

  if (completedAllForRace && currentRace) {
    return (
      <div className="min-h-screen bg-background p-4 sm:p-6">
        <div className="max-w-2xl mx-auto">
          <div className="blue-sky-card rounded-2xl p-6 sm:p-8 text-center">
            <div className="text-6xl mb-6">🎉</div>
            <h2 className="text-xl sm:text-2xl font-bold text-white mb-4">
              {t('formula1.completedAllPredictions')}
            </h2>
            <p className="text-white/80 text-small sm:text-body mb-6">
              {t('formula1.completedAllPredictionsDesc')}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => navigate(`/formula1/${roomId}`)}
                className="px-6 sm:px-8 py-2.5 sm:py-3 bg-primary hover:opacity-90 text-primary-foreground rounded-xl font-semibold transition-opacity text-small sm:text-nav"
              >
                {t('predictions.backToRoomButton')}
              </button>
              {hasNextRace && (
                <button
                  onClick={() => {
                    setSelectedRaceIndex((i) => i + 1)
                    setSelectedPositionIndex(0)
                    setPositionToDriver({})
                    setPrediction(null)
                    setEditingPosition(null)
                  }}
                  className="px-6 sm:px-8 py-2.5 sm:py-3 bg-white/10 hover:bg-white/20 text-white rounded-xl font-semibold transition-opacity text-small sm:text-nav"
                >
                  {t('formula1.nextRace')}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background p-4 sm:p-6">
      <div className="max-w-2xl mx-auto">
        <Link
          to={`/formula1/${roomId}`}
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-4 sm:mb-6 text-small sm:text-body"
        >
          <ArrowLeft className="w-4 h-4 shrink-0" />
          {t('predictions.backToRoom')}
        </Link>

        <div className="blue-sky-card rounded-2xl p-6 sm:p-8">
          <div className="flex items-center justify-between w-full mb-6">
            <div className="flex gap-1 sm:gap-1.5">
              {Array.from({ length: POSITIONS }).map((_, i) => (
                <div
                  key={i}
                  className={`h-2 w-4 sm:w-5 rounded-sm flex-shrink-0 ${
                    i < selectedPositionIndex ? 'bg-primary' : i === selectedPositionIndex ? 'bg-primary/80' : 'bg-white/20'
                  }`}
                />
              ))}
            </div>
            <span className="text-white/80 font-semibold text-small sm:text-body ml-2">
              {currentPosition}/{POSITIONS}
            </span>
          </div>

          <div className="mb-4">
            <span className="text-4xl sm:text-5xl inline-block mb-2">🏎️</span>
            <h1 className="text-xl sm:text-2xl font-bold text-white">
              {t('formula1.playingRoomPredictions')}
            </h1>
          </div>

          {currentRace && (
            <>
              <p className="text-white/80 text-small sm:text-body mb-6">
                Race {currentRace.day}: {currentRace.city}
                {currentRace.raceDate && (
                  <span className="ml-2">
                    · {new Date(currentRace.raceDate).toLocaleDateString()}
                  </span>
                )}
              </p>

              {roomId && raceId && (
                <F1GridCopilotPanel
                  roomId={roomId}
                  raceId={raceId}
                  onApplyProposal={handleApplyF1Grid}
                />
              )}

              <div className="flex items-center justify-center gap-4 mt-6">
                <button
                  type="button"
                  onClick={() => setSelectedPositionIndex((i) => Math.max(0, i - 1))}
                  disabled={selectedPositionIndex === 0}
                  className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-40 disabled:pointer-events-none flex items-center justify-center text-white transition-colors"
                  aria-label="Previous position"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <span className="text-white font-semibold">
                  {currentPosition === 1 && '1st'}
                  {currentPosition === 2 && '2nd'}
                  {currentPosition === 3 && '3rd'}
                  {currentPosition > 3 && `${currentPosition}th`} place
                </span>
                <button
                  type="button"
                  onClick={() => setSelectedPositionIndex((i) => Math.min(POSITIONS - 1, i + 1))}
                  disabled={selectedPositionIndex >= POSITIONS - 1}
                  className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-40 disabled:pointer-events-none flex items-center justify-center text-white transition-colors"
                  aria-label="Next position"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </>
          )}
        </div>

        {currentRace && (
          <>
            <Formula1SelectPositionModal
              isOpen={showSelectModal}
              position={currentPosition}
              availableDrivers={availableDriversForPosition}
              onClose={handleCloseSelectModal}
              onSelect={handleSelectDriver}
            />
            <Formula1ExistedPositionModal
              isOpen={showExistedModal && !!positionToDriver[currentPosition]}
              position={currentPosition}
              driverName={positionToDriver[currentPosition] ?? ''}
              onKeepClick={handleKeepPosition}
              onChangeClick={handleChangePosition}
            />
          </>
        )}
      </div>
    </div>
  )
}

export default Formula1Playing
